import { useEffect, useId, useState } from 'react'
import { cn } from '@/lib/utils'

/** Spread onto each focusable element the tooltip describes. */
export interface TooltipTargetProps {
  'aria-describedby': string
}

export interface TooltipProps {
  content: React.ReactNode
  /** Renders the region; the targets inside it take the description props. */
  children: (target: TooltipTargetProps) => React.ReactNode
  className?: string
}

/**
 * Shows `content` while the pointer is over, or keyboard focus is within, the wrapped
 * region. The focusable targets inside it carry the description, so keyboard and
 * screen-reader users get the same message without hovering. Escape dismisses it until
 * the pointer or focus re-enters (WCAG 1.4.13).
 */
export function Tooltip({ content, children, className }: TooltipProps) {
  const id = useId()
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (dismissed) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDismissed(true)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [dismissed])

  const reveal = () => setDismissed(false)

  return (
    <div className={cn('group relative', className)} onMouseEnter={reveal} onFocus={reveal}>
      {children({ 'aria-describedby': id })}
      <div
        id={id}
        role="tooltip"
        className={cn(
          'invisible absolute top-1/3 left-0 z-10 max-w-xs rounded-lg bg-tooltip px-5 py-3 text-sm font-semibold text-inverse opacity-0 shadow-panel transition-opacity',
          !dismissed &&
            'group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100',
        )}
      >
        {content}
      </div>
    </div>
  )
}
